import { Button } from "@/components/Button";
import { Divider } from "@/components/Divider";
import {
  AboutUsIcon,
  CareerIcon,
  ContactUsIcon,
  DownArrow,
  PrivacyPolicyIcon,
  RefundPolicyIcon,
  RightArrow,
  TermOfUseIcon,
} from "@/components/Icons";
import { Column, Row } from "@/components/Stack";
import { Text } from "@/components/Text";
import { DialectsEnum } from "@/constants/constants";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import {
  isTvEnv,
  scrollCurrentFocusedElementIntoView,
} from "@/utils/common.helpers";
import { useFocusable } from "@noriginmedia/norigin-spatial-navigation";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useState } from "react";

export const PolicyAboutUs = ({
  isExpanded = false,
  variant = "default",
  hasBg = false,
}: {
  isExpanded?: boolean;
  variant?: "default" | "light";
  hasBg?: boolean;
}) => {
  const [isExpandedState, setIsExpandedState] = useState(isExpanded);
  const { t, currentLanguage } = useClientTranslations();
  const pathname = usePathname();

  useEffect(() => {
    setIsExpandedState(isExpanded);
  }, [isExpanded]);

  // Extract dialect from pathname (works in all contexts)
  const pathParts = pathname.split("/").filter(Boolean);
  const dialectFromPath = pathParts.find((part) =>
    Object.values(DialectsEnum).includes(part as DialectsEnum),
  ) as DialectsEnum | undefined;

  // Use dialect from pathname, or default to HARYANVI
  const dialectUrlValue: string = dialectFromPath || DialectsEnum.HARYANVI;
  const linkPrefix = `/${currentLanguage}`;

  const textColor = variant === "light" ? "text-white" : "text-stg-grey-5";
  const iconType = variant === "light" ? "white" : "default";
  const arrowStroke = variant === "light" ? "white" : "grey5";

  const links = [
    {
      href: "/about-us",
      label: t("setting_page.about_us"),
      icon: <AboutUsIcon type={iconType} />,
      visibleOnTv: true,
    },
    {
      href: "https://stage.zohorecruit.in/jobs/Careers",
      label: t("setting_page.career"),
      icon: <CareerIcon type={iconType} />,
      target: "_blank" as const,
      visibleOnTv: false,
    },
    {
      href: "/privacy",
      label: t("setting_page.privacy_policy"),
      icon: <PrivacyPolicyIcon type={iconType} />,
      visibleOnTv: true,
    },
    {
      href: linkPrefix + `/${dialectUrlValue}/settings/contact-us`,
      label: t("setting_page.contact_us"),
      icon: <ContactUsIcon type={iconType} />,
      visibleOnTv: true,
    },
    {
      href: "/terms",
      label: t("setting_page.term_of_use"),
      icon: <TermOfUseIcon type={iconType} />,
      visibleOnTv: true,
    },
    {
      href: "/refund-policy",
      label: t("auth_screen.refund_policy"),
      icon: <RefundPolicyIcon type={iconType} />,
      visibleOnTv: true,
    },
  ];

  if (isTvEnv())
    return (
      <PolicyAboutUsTv
        links={links.filter((link) => link.visibleOnTv)}
        hasBg={hasBg}
      />
    );

  return (
    <div className="w-full">
      <button
        onClick={() => {
          setIsExpandedState(!isExpandedState);
        }}
        className="flex w-full flex-row items-center justify-between transition-opacity hover:opacity-80"
      >
        <Text size={5} weight="bold" alignment="center">
          <span className={textColor}>{t("setting_page.accordian_title")}</span>
        </Text>
        <div className={`settings-arrow ${isExpandedState ? "open" : null}`}>
          <DownArrow stroke={arrowStroke} />
        </div>
      </button>
      <div
        className={`hide-scrollbar settings-expander grid w-full overflow-y-auto pt-4 ${isExpandedState ? "expanded" : null}`}
      >
        <div className="settings-expander-content w-full">
          <Column gap={2} fullWidth useGrid justify="stretch">
            {links.map((link, index) => (
              <div key={link.href}>
                <Column gap={2} fullWidth useGrid justify="stretch">
                  <LinkUnit {...link} variant={variant} />
                  {index < links.length - 1 && (
                    <Divider hasMargin={false} className="!bg-stg-grey-1" />
                  )}
                </Column>
              </div>
            ))}
          </Column>
        </div>
      </div>
    </div>
  );
};

const LinkUnit = ({
  href,
  label,
  icon,
  variant = "default",
  target,
}: {
  href: string;
  label: string;
  icon: React.ReactNode;
  variant?: "default" | "light";
  target?: "_blank";
}) => {
  const textColor = variant === "light" ? "text-white" : "text-stg-grey-5";

  return (
    <Link
      className="flex w-full flex-row items-center justify-start gap-3 transition-opacity hover:opacity-80"
      href={href}
      {...(target && { target })}
    >
      <div className="h-6 w-6">{icon}</div>
      <Text size={3} weight="bold" alignment="left">
        <span className={textColor}>{label}</span>
      </Text>
    </Link>
  );
};

const PolicyAboutUsTv = ({
  links,
  hasBg = false,
}: {
  links: {
    href: string;
    label: string;
    icon: React.ReactNode;
    target?: "_blank";
  }[];
  hasBg?: boolean;
}) => {
  const { t } = useClientTranslations();
  return links.map((link) => (
    <PolicyAboutUsTvLink key={link.href} {...link} hasBg={hasBg} />
  ));
};

const PolicyAboutUsTvLink = ({
  href,
  label,
  hasBg = false,
}: {
  href: string;
  label: string;
  hasBg?: boolean;
}) => {
  const router = useRouter();
  const { ref, focused } = useFocusable({
    focusKey: `policy-about-us-link-${href}`,
    onEnterPress: () => {
      router.push(href);
    },
    onFocus: (e) => scrollCurrentFocusedElementIntoView(e),
  });

  return (
    <Button
      ref={ref}
      fullWidthChildren={true}
      variant="outlined-white"
      href={href}
      className={`rounded-xl !border-2 ${focused ? "!border-white" : "!border-stg-grey-3"} !px-4 transition-all duration-200 ${hasBg ? "!bg-stg-grey-2 bg-opacity-40" : ""}`}
    >
      <Row justify="spaceBetween" align="center" fullWidth>
        <Text weight="medium">
          <span className={`text-2xl`}>{label}</span>
        </Text>

        <RightArrow width={20} height={20} />
      </Row>
    </Button>
  );
};
